import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import Sparkline from "../components/Sparkline";
import StreakCard from "../components/StreakCard";

const STATUS_COLORS = {
  queued: "text-neutral-400",
  extracting_frames: "text-yellow-400",
  analyzing: "text-yellow-400",
  complete: "text-green-500",
  failed: "text-red-500",
};

export default function Dashboard() {
  const { user, usage, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [code, setCode] = useState("");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    api.get("/analyses").then((r) => setItems(r.data.analyses || [])).finally(() => setLoading(false));
    api.get("/coach/my-coaches").then((r) => setCoaches(r.data.coaches || [])).catch(() => {});
  }, []);

  const linkCoach = async () => {
    if (!code.trim()) return;
    setLinking(true);
    try {
      const r = await api.post("/coach/link-by-code", { code: code.trim() });
      toast.success(r.data.already_linked ? "Already linked" : `Linked with ${r.data.coach_name}`);
      setCode("");
      const c = await api.get("/coach/my-coaches"); setCoaches(c.data.coaches || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link"); }
    finally { setLinking(false); }
  };
  const unlinkCoach = async (id) => {
    await api.delete(`/coach/my-coaches/${id}`);
    toast.success("Coach removed");
    const c = await api.get("/coach/my-coaches"); setCoaches(c.data.coaches || []);
  };

  const toggleDigest = async (checked) => {
    await api.patch("/auth/me/prefs", { digest_enabled: checked });
    toast.success(checked ? "Weekly recap on" : "Weekly recap off");
    refresh();
  };

  return (
    <div>
      <div className="rs-underline mb-3" />
      <h1 className="font-display-heavy text-4xl">HEY {(user?.name || "").split(" ")[0]?.toUpperCase() || "ATHLETE"}</h1>
      <p className="text-neutral-400 mt-2">Ready to snap another rep?</p>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StreakCard />
        <Sparkline />
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Connect a coach</div>
          <p className="text-neutral-400 text-sm mt-2">Enter your coach's 8-char code to give them read-only access.</p>
          <div className="mt-3 flex gap-2">
            <Input data-testid="coach-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABCDEFGH" className="h-11 bg-neutral-950 border-neutral-700 font-mono tracking-widest uppercase" maxLength={8} />
            <Button data-testid="coach-link-btn" onClick={linkCoach} disabled={linking || code.length < 4} className="h-11 bg-red-600 hover:bg-red-700 font-semibold">Link</Button>
          </div>
          {coaches.length > 0 && (
            <div className="mt-3 space-y-1">
              {coaches.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-300">✓ {c.name}</span>
                  <button data-testid={`unlink-coach-${c.id}`} onClick={() => unlinkCoach(c.id)} className="text-neutral-500 hover:text-red-500 text-xs uppercase tracking-widest">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Weekly recap</div>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <div className="font-display-heavy text-lg">Auto email · Mondays</div>
              <div className="text-xs text-neutral-500">Sent 9am {user?.digest_tz || "UTC"}</div>
            </div>
            <Switch data-testid="digest-toggle" checked={user?.digest_enabled !== false} onCheckedChange={toggleDigest} />
          </div>
          <Link to="/progress"><Button variant="ghost" className="mt-3 h-10 w-full text-neutral-300 hover:text-white hover:bg-neutral-800 justify-start">View this week's recap →</Button></Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">This month · Free</div>
          <div data-testid="free-remaining-value" className="font-display-heavy text-5xl mt-2">{usage?.free_remaining ?? "—"}<span className="text-neutral-500 text-2xl">/{usage?.free_limit ?? 3}</span></div>
          <div className="text-neutral-400 text-sm mt-2">Resets at the start of each month.</div>
        </div>
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Credit Balance</div>
          <div data-testid="credit-balance-value" className="font-display-heavy text-5xl mt-2">{usage?.credit_balance ?? 0}</div>
          <div className="text-neutral-400 text-sm mt-2">Never expires.</div>
        </div>
        <div className="border border-neutral-800 rounded-md bg-neutral-900 p-6">
          <div className="uppercase text-xs text-neutral-400 font-display tracking-widest">Subscription</div>
          <div data-testid="subscription-value" className="font-display-heavy text-4xl mt-2">{usage?.unlimited ? "UNLIMITED" : "—"}</div>
          <div className="text-neutral-400 text-sm mt-2">{usage?.unlimited ? "Rep as much as you want." : "Go unlimited to lift the cap."}</div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/upload"><Button data-testid="dashboard-upload-btn" className="h-12 px-6 bg-red-600 hover:bg-red-700 font-semibold">+ New Analysis</Button></Link>
        <Link to="/plan"><Button data-testid="dashboard-plan-btn" variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">View my plan</Button></Link>
        <Link to="/progress"><Button data-testid="dashboard-progress-btn" variant="outline" className="h-12 px-6 border-neutral-700 text-white hover:bg-neutral-900">This week's recap</Button></Link>
      </div>

      <div className="mt-12">
        <div className="rs-underline mb-3" />
        <h2 className="font-display-heavy text-2xl">RECENT ANALYSES</h2>
        {loading ? <div className="text-neutral-400 mt-4">Loading…</div> : items.length === 0 ? (
          <div className="mt-4 border border-dashed border-neutral-800 rounded-md p-8 text-center">
            <p className="text-neutral-400">Nothing here yet. Upload your first clip.</p>
          </div>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {items.map((a) => (
              <Link data-testid={`analysis-card-${a.id}`} key={a.id} to={`/analysis/${a.id}`} className="border border-neutral-800 rounded-md bg-neutral-900 p-5 hover:border-red-700 transition-colors block">
                <div className="flex justify-between items-start">
                  <div className="font-display-heavy text-xl">{a.sport}</div>
                  <span className={`text-xs uppercase font-display font-bold tracking-widest ${STATUS_COLORS[a.status] || "text-neutral-400"}`}>{a.status.replace("_", " ")}</span>
                </div>
                <div className="text-sm text-neutral-400 mt-1">{new Date(a.created_at).toLocaleString()}</div>
                {a.result?.summary && <div className="text-sm text-neutral-300 mt-3 line-clamp-2">{a.result.summary}</div>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
